import type {
  ChainEnum,
  WalletTransactionsResponse,
} from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../client/core/getCore';
import { getDefaultClient } from '../../../../client/defaultClient';
import { createApiClient } from '../../../apiClient';
import type { Chain } from '../../../chain/chain.types';

export type GetTransactionHistoryParams = {
  address: string;
  chain: Chain;
  limit?: number;
  networkId: number;
  offset?: string;
};

export type GetTransactionHistoryResponse = WalletTransactionsResponse;

/**
 * Retrieves the transaction history for a given wallet address, chain and network.
 *
 * This function fetches the transaction history for a specified wallet address and chain,
 * returning a list of transactions associated with that address along with nextOffset for pagination.
 *
 * @param params.address - The wallet address to query transactions for.
 * @param params.chain - The chain to query transactions for.
 * @param [params.limit] - The maximum number of transactions to return.
 * @param params.networkId - The network ID to query transactions for.
 * @param [params.offset] - The offset to use for pagination.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the transaction history along with nextOffset for the given wallet address and chain.
 */
export const getTransactionHistory = async (
  params: GetTransactionHistoryParams,
  client = getDefaultClient()
): Promise<WalletTransactionsResponse> => {
  const core = getCore(client);
  const apiClient = createApiClient({}, client);

  const response = await apiClient.getWalletTransactions({
    address: params.address,
    // eslint-disable-next-line custom-rules/ban-chain-enum
    chainName: params.chain as ChainEnum,
    environmentId: core.environmentId,
    networkId: params.networkId,
  });

  return response;
};
